// src/pages/GuestDetailsPage.tsx
import React, { useContext, useEffect, useState } from "react";
import { Box, IconButton, Typography, Container, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";

import PrevGuestIcon from "../assets/icons/guest_det_prev_guest.svg";
import NextGuestIcon from "../assets/icons/guest_det_next_guest.svg";
import GuestDetailsForm from "../components/GuestDetailsForm";
import UnsavedChangesDialog from "../components/UnsavedChangesDialog";
import ContentContainer from "./ContentContainer";
import { useTranslation } from "react-i18next";
import useNavigateWithLang from "../hooks/useNavigateWithLang";
import { AppContext } from "../context/AppContext";
import { fetchGuestById } from "../api/GuestService";
import { GuestFormDataWithCompletion } from "../models/StaticDataModel";
import { hasAllMandatoryData, isOperaGuestProfileId } from "../utils/guestUtils"; // ← reuse shared helpers

const GuestDetailsPage: React.FC = () => {
    const { state, updateGuest, setIsFormDirty } = useContext(AppContext) ?? {};
    const { navigateWithLang } = useNavigateWithLang();
    const { guestId } = useParams<{ guestId: string }>();
    const [guestData, setGuestData] = useState<GuestFormDataWithCompletion | null>(null);
    const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

    const isRealBackendId = (g: GuestFormDataWithCompletion) =>
        isOperaGuestProfileId(g.id, g.checkInSubmissionId);
    // ✅ Deterministic flags:
    // - updateUser = !isEmptyGuest(g)   (never null/undefined)
    // - completed  = hasAllMandatoryData(g), email required only for slot 0
    const ensureFlags = (
        g: GuestFormDataWithCompletion,
        requireContact: boolean
    ): GuestFormDataWithCompletion => {
        const completed = hasAllMandatoryData(g, undefined, { requireContact });
        const updateUser = isRealBackendId(g) ? true : false; // real id -> true, placeholder -> false
        return { ...g, completed, updateUser };
    };

    useEffect(() => {
        const fetchData = async () => {
            const index = Number(guestId) - 1;
            const requireContact = index === 0;
            const fallback = state?.guests?.[index];

            if (!fallback) {
                console.warn("❌ No guest found at index", index);
                setGuestData(null);
                return;
            }

            if (isRealBackendId(fallback)) {
                try {
                    const data = await fetchGuestById(String(fallback.id));
                    const normalized = ensureFlags(data, requireContact);
                    updateGuest?.(normalized);
                    setGuestData(normalized);
                } catch (err) {
                    console.warn("❌ Failed to fetch guest by ID; using fallback", err);
                    const normalized = ensureFlags(fallback, requireContact);
                    updateGuest?.(normalized);
                    setGuestData(normalized);
                }
            } else {
                const normalized = ensureFlags(fallback, requireContact);
                updateGuest?.(normalized);
                setGuestData(normalized);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guestId]);

    const guests = state?.guests ?? [];
    const totalGuests = guests.length;
    const guestIndex = Number(guestId) - 1;

    const { t: tGuestDetails } = useTranslation("guest_details");
    const { t: tNavButtons } = useTranslation("guests_navigation_buttons");

    /**
     * Runs `action` immediately when the form is clean, otherwise stashes it
     * and opens the unsaved-changes dialog so the user can confirm.
     */
    const guardNavigation = (action: () => void) => {
        if (state?.isFormDirty) {
            setPendingNavigation(() => action);
            return;
        }
        action();
    };

    const handlePreviousGuestClick = () => {
        if (guestIndex > 0) {
            guardNavigation(() => navigateWithLang(`/guest-details/${guestIndex}`));
        }
    };

    const handleNextGuestClick = () => {
        if (guestIndex < totalGuests - 1) {
            guardNavigation(() => navigateWithLang(`/guest-details/${guestIndex + 2}`));
        }
    };

    const handleClose = () => guardNavigation(() => navigateWithLang("/guest-info/"));

    const handleStayOnPage = () => setPendingNavigation(null);

    const handleConfirmLeave = () => {
        const action = pendingNavigation;
        setPendingNavigation(null);
        setIsFormDirty?.(false);
        action?.();
    };

    // Warn on tab close / refresh / external navigation when there are unsaved changes
    useEffect(() => {
        if (!state?.isFormDirty) return;
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            // Modern browsers ignore the custom string but require returnValue to be set.
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [state?.isFormDirty]);

    return (
        <ContentContainer
            leftContainerIcon={
                guestIndex > 0 ? (
                    <img src={PrevGuestIcon} alt="Previous Guest Icon" width={48} height={48} />
                ) : (
                    <Box sx={{ width: 48, height: 48 }} />
                )
            }
            leftContainerText={guestIndex > 0 ? tNavButtons("previous_guest") : " "}
            onLeftContainerClick={guestIndex > 0 ? handlePreviousGuestClick : undefined}
            rightContainerIcon={
                guestIndex < totalGuests - 1 ? (
                    <img src={NextGuestIcon} alt="Next Guest Icon" width={48} height={48} />
                ) : (
                    <Box sx={{ width: 48, height: 48 }} />
                )
            }
            rightContainerText={guestIndex < totalGuests - 1 ? tNavButtons("next_guest") : " "}
            onRightContainerClick={guestIndex < totalGuests - 1 ? handleNextGuestClick : undefined}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    maxWidth: "1200px",
                    margin: "0 auto",
                }}
            >
                {/* Title Section */}
                <Container
                    maxWidth="lg"
                    sx={{
                        textAlign: "left",
                        mb: 3,
                        mt: 5,
                        display: "flex",
                        position: "relative",
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: "'Dala Floda', serif",
                            fontSize: "2.5rem",
                            fontWeight: "bold",
                        }}
                    >
                        {tGuestDetails("pageTitle")}
                    </Typography>

                    {/* Close Button in Top-Right Corner */}
                    <Tooltip title="Close and return to guest info">
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                color: "#333",
                                "&:hover": { color: "#7C624B" },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Container>

                {/* Content */}
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <GuestDetailsForm guest={guestData} />
                    </Box>
                </Container>
            </Box>

            <UnsavedChangesDialog
                open={pendingNavigation !== null}
                onStay={handleStayOnPage}
                onLeave={handleConfirmLeave}
            />
        </ContentContainer>
    );
};

export default GuestDetailsPage;
